import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  brandSearchUrl,
  platformFromKey,
  platformKeys,
  platformKeysFor,
  platformSupports,
  platformSupportsManualPdp,
} from "../api/platforms";
import {
  getBrandSkusExportV1BrandsSkusExportGetUrl,
  getBrandSkusV1BrandsSkusGetQueryKey,
  getGetBrandV1BrandsBrandIdGetQueryKey,
  getSkuEventsV1SkuEventsGetQueryKey,
  probeSkuUrlV1BrandsBrandIdSkusProbeUrlPost,
  searchProbeV1BrandsSearchProbePost,
  useAddCandidateV1BrandsBrandIdCandidatesPost,
  useAddSkuUrlV1BrandsBrandIdSkusAddUrlPost,
  useBrandSkusV1BrandsSkusGet,
  useDeleteCandidateV1BrandsBrandIdCandidatesCandidateIdDelete,
  useGetBrandV1BrandsBrandIdGet,
  useLaunchBrandSweepV1BrandsBrandIdSweepPost,
  useLaunchTaxonomyClassifyV1TaxonomyClassifyPost,
  useRemoveSkuV1BrandsBrandIdSkusRemovePost,
  useSetForceAllStoresV1BrandsSetForceAllStoresPost,
  useSetSkuBundleV1BrandsBrandIdSkusSetBundlePost,
  useSetSkuEnabledV1BrandsBrandIdSkusSetEnabledPost,
  useSkuEventsV1SkuEventsGet,
  useUpdateBrandV1BrandsBrandIdPatch,
  useUpdateCandidateV1BrandsBrandIdCandidatesCandidateIdPatch,
} from "../api/generated/qcsControlPlane";
import { countFor as countOf, fetchSkuCounts } from "../api/skuCounts";
import {
  Flow,
  type BrandCandidateOut,
  type BrandSweepLaunchResult,
  type SearchProbeResult,
  type Sku,
  type SkuEvent,
  type SkuUrlProbeResult,
} from "../api/generated/model";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { SelectAllCheckbox, SelectionCount, useRowSelection } from "../lib/selection";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { BrandMatchGrid } from "../components/BrandMatchGrid";
import { BrandNameEditor } from "../components/BrandNameEditor";
import { PriorityStepper } from "../components/PriorityStepper";
import { SweepConfigModal } from "../components/SweepConfigModal";
import { IconExternal } from "../components/icons";
import { ProbeReport } from "../components/CandidateEditor";
import { useToast } from "../components/toast";
import { Badge, Button, ErrorState, Input, PageHeader, Panel, Row, Switch, type DotTone as Tone } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { PlayIcon, Seg } from "../components/foundation";
import { useCanWrite } from "../auth/me";
import {
  type Platform,
  ago,
  fmtDateTime,
  fmtDateTimeSec,
  pdpUrl,
  platformLabel,
} from "../api/labels";

const EVENT_TONE: Record<string, Tone> = {
  added: "ok",
  reactivated: "info",
  removed: "warn",
  excluded: "crit",
};

// Hover text for the timestamp columns: full precision plus how long ago, since the cell
// itself is trimmed to date + minute to fit the column.
const timestampTitle = (iso: string | null | undefined) =>
  iso ? `${fmtDateTimeSec(iso)} (${ago(iso)})` : "never seen";

type Notify = ReturnType<typeof useToast>;
type Navigate = ReturnType<typeof useNavigate>;

// Join the labelled platforms of a given launch status, for the summary toast.
function platformsWith(result: BrandSweepLaunchResult, status: string): string {
  return (result.items ?? [])
    .filter((i) => i.status === status)
    .map((i) => platformLabel(i.platform))
    .join(", ");
}

// One handler for all three launch buttons (brand-level, keyword-level, all-platforms):
// exactly one run -> jump to the live run monitor; a fan-out -> summary toast; nothing
// launched -> the skip reason (already-running / no-candidates).
function handleSweepResult(result: BrandSweepLaunchResult, notify: Notify, navigate: Navigate) {
  const launched = (result.items ?? []).filter((i) => i.status === "launched");
  const busy = platformsWith(result, "already_running");
  const empty = platformsWith(result, "no_candidates");
  // planning/48 Phase 5: a PDP-refresh launch skips Instamart (no product-page stage).
  const noPdp = platformsWith(result, "no_pdp_stage");
  if (launched.length === 1 && launched[0].run_id) {
    notify("Sweep started", "ok");
    navigate(`/runs/${launched[0].run_id}`);
    return;
  }
  if (launched.length > 1) {
    const skipped = [
      busy && `already running: ${busy}`,
      empty && `no candidates: ${empty}`,
      noPdp && `no PDP stage: ${noPdp}`,
    ]
      .filter(Boolean)
      .join("; ");
    const head = `Started ${launched.length} sweeps: ${platformsWith(result, "launched")}`;
    notify(skipped ? `${head} (${skipped})` : head, "ok");
    return;
  }
  // Nothing launched.
  if (busy) notify(`already running: ${busy}`, "info");
  else if (empty) notify("no enabled candidates to sweep", "info");
  else if (noPdp) notify(`PDP refresh skipped (no product pages): ${noPdp}`, "info");
  else notify("nothing to sweep", "info");
}

// Shared launch mutation for the ad-hoc brand-sweep buttons (planning/41). Refreshes the
// per-platform SKU counts on success and routes the result through handleSweepResult.
// (brandId is passed per-call to `mutate`, so it is not needed here.)
function useSweepLauncher() {
  const notify = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  return useLaunchBrandSweepV1BrandsBrandIdSweepPost<Error>({
    mutation: {
      onSuccess: (result) => {
        qc.invalidateQueries({ queryKey: ["sku-counts"] });
        handleSweepResult(result, notify, navigate);
      },
      onError: (e) => notify(e instanceof Error ? e.message : "launch failed", "crit"),
    },
  });
}

// Per-brand detail page: one tab per platform, each listing that platform's
// current active SKUs (the dimension rows behind the count) plus its lifecycle
// timeline. Reached from the Brands table (brand name / platform pill -> here).
export function BrandDetail() {
  const { brandId = "" } = useParams<{ brandId: string }>();
  const [params, setParams] = useSearchParams();

  const brandQ = useGetBrandV1BrandsBrandIdGet(brandId, {
    query: { retry: false },
  });
  const brand = brandQ.data?.brand ?? "";
  useBreadcrumbTail([{ label: brand || "…" }]);

  const qc = useQueryClient();
  const notify = useToast();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const setPriority = useUpdateBrandV1BrandsBrandIdPatch({
    mutation: {
      onSuccess: (data) => {
        notify(`${data.brand} priority ${data.priority}`);
        qc.invalidateQueries({
          queryKey: getGetBrandV1BrandsBrandIdGetQueryKey(brandId),
        });
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });

  // planning/47: per-brand "force all stores" for cart inventory (probe this brand's SKUs in
  // every active store even when the run has the carriage filter on).
  const setForceAllStores = useSetForceAllStoresV1BrandsSetForceAllStoresPost({
    mutation: {
      onSuccess: (_r, vars) => {
        notify(vars.data.force_all_stores ? "Forcing all stores (cart)" : "Using store-sku filter", "ok");
        qc.invalidateQueries({
          queryKey: getGetBrandV1BrandsBrandIdGetQueryKey(brandId),
        });
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });

  // planning/47: classify this brand's SKUs into the taxonomy (brand-scoped classify, all
  // platforms). Fan-out; a single launched run navigates to the monitor.
  const classifyBrand = useLaunchTaxonomyClassifyV1TaxonomyClassifyPost({
    mutation: {
      onSuccess: (
        result:
          | BrandSweepLaunchResult
          | {
              items?: {
                platform: string;
                status: string;
                run_id?: string | null;
              }[];
            },
      ) => {
        const launched = (result.items ?? []).filter((i) => i.status === "launched");
        if (launched.length === 1 && launched[0].run_id) {
          notify("Classify started", "ok");
          navigate(`/runs/${launched[0].run_id}`);
        } else if (launched.length > 0) {
          notify(`Started ${launched.length} classify run(s)`, "ok");
        } else {
          notify("already running", "info");
        }
      },
      onError: (e) => notify(e instanceof Error ? e.message : "launch failed", "crit"),
    },
  });

  // Ad-hoc "sweep this brand on every platform" (planning/41): fans out one run per
  // platform that has an enabled candidate. Disabled when the brand has none anywhere.
  const allSweep = useSweepLauncher();
  const [allCfgOpen, setAllCfgOpen] = useState(false);
  const brandSweepPlatforms = platformKeysFor(Flow.brand_sweep);
  const hasAnyEnabledCandidate = (brandQ.data?.candidates ?? []).some(
    (c) => c.enabled && brandSweepPlatforms.includes(c.platform),
  );

  // per-platform active SKU counts for this brand -> tab badges.
  // Tab badges + the per-platform "N active" header exclude combos by default, matching
  // the Brands-page pills and this page's default-hidden SKU table (below). Keyed on the
  // combos flag so it shares cache with the Brands page's excl-combos query.
  const countsQ = useQuery({
    queryKey: ["sku-counts", false],
    queryFn: () => fetchSkuCounts(false),
    refetchInterval: 30000,
  });
  // The tab pills show the brand's footprint per platform (`total`), which is what "does
  // this brand have SKUs here" means; the per-SKU enable state is a column in the table
  // below, so netting it out of the tab count would hide SKUs the tab is about to show.
  const countFor = (p: Platform) => (brand ? countOf(countsQ.data, p, brand).total : 0);

  // The tab axis is per-platform, but a match spans platforms - so "matched" sits beside
  // them as a peer rather than inside any one of them.
  const MATCH_TAB = "matched" as const;
  const tabParam = params.get("tab");
  const [tab, setTab] = useState<Platform | typeof MATCH_TAB>(
    tabParam === MATCH_TAB
      ? MATCH_TAB
      : (platformFromKey(tabParam ?? "") ?? platformKeys()[0]),
  );
  const platformTab = tab === MATCH_TAB ? platformKeys()[0] : tab;
  const selectTab = (p: Platform | typeof MATCH_TAB) => {
    setTab(p);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", p);
      return next;
    });
  };

  // First time counts land (and no explicit ?tab), jump to the platform with the
  // most SKUs so the page opens on something non-empty.
  const [autoPicked, setAutoPicked] = useState(false);
  useEffect(() => {
    if (autoPicked || tabParam || !countsQ.data) return;
    const best = [...platformKeys()].sort((a, b) => countFor(b) - countFor(a))[0];
    if (best && countFor(best) > 0) setTab(best);
    setAutoPicked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countsQ.data, autoPicked, tabParam]);

  const head = (
    <>
      <PageHeader
        className="mb-3.5"
        title={<BrandNameEditor brandId={brandId} brand={brand} />}
        titleMeta={
          <>
          {brandQ.data && (
            <Badge variant={toneVariant(brandQ.data.enabled ? "ok" : "idle")}>
              {brandQ.data.enabled ? "active" : "inactive"}
            </Badge>
          )}
          {brandQ.data && (
            <div className="row" style={{ gap: 7, alignItems: "center" }}>
              <span className="faint" style={{ fontSize: 12 }}>
                priority
              </span>
              <PriorityStepper
                value={brandQ.data.priority}
                disabled={setPriority.isPending}
                onCommit={(priority) => {
                  if (priority !== brandQ.data?.priority)
                    setPriority.mutate({ brandId, data: { priority } });
                }}
              />
            </div>
          )}
          {brandQ.data && !canWrite && (
            <Badge variant={brandQ.data.force_all_stores ? "outline-warning" : "outline"}>
              {brandQ.data.force_all_stores ? "force all stores" : "store-sku filter"}
            </Badge>
          )}
          {brandQ.data && canWrite && (
            <div className="row" style={{ gap: 7, alignItems: "center" }}>
              <span className="faint" style={{ fontSize: 12 }}>force all stores</span>
              <Switch
                checked={brandQ.data.force_all_stores ?? false}
                disabled={setForceAllStores.isPending}
                aria-label="Cart inventory: probe this brand's SKUs in every store (bypass the store-sku filter)"
                title={
                  brandQ.data.force_all_stores
                    ? "Cart probes this brand in EVERY store (store-sku filter bypassed). Click to use the filter."
                    : "Cart uses the store-sku carriage filter. Click to force all stores for this brand."
                }
                onCheckedChange={(next) =>
                  setForceAllStores.mutate({
                    data: { force_all_stores: next, ids: [brandId] },
                  })
                }
              />
            </div>
          )}
          </>
        }
        description={
          <>
          Tracked brand · swept on every platform ·{" "}
          <Link to="/brands" className="as-link">all brands</Link>
          </>
        }
        action={
          brandQ.data && canWrite && (
            <Row gap="sm" wrap className="justify-end">
              <Button
                variant="primary"
                size="sm"
                loading={allSweep.isPending}
                disabled={!hasAnyEnabledCandidate}
                title={
                  hasAnyEnabledCandidate
                    ? "Launch a one-off sweep of this brand on every platform that has an enabled candidate"
                    : "No enabled search candidate on any platform"
                }
                onClick={() => setAllCfgOpen(true)}
              >
                {allSweep.isPending ? "Starting…" : <>Sweep on all platforms <PlayIcon /></>}
              </Button>
              <Button
                variant="outline"
                size="sm"
                loading={classifyBrand.isPending}
                title="Classify this brand's SKUs into the taxonomy (all platforms)"
                onClick={() => classifyBrand.mutate({ data: { brand, mode: "missing" } })}
              >
                {classifyBrand.isPending ? "Starting…" : "Classify this brand"}
              </Button>
            </Row>
          )
        }
      />
      {canWrite && allCfgOpen && (
        <SweepConfigModal
          scopeLabel={`Sweep ${brand} on all platforms`}
          note="One run per platform with an enabled candidate (brand-level, authoritative)."
          targetPlatforms={brandSweepPlatforms}
          pending={allSweep.isPending}
          onClose={() => setAllCfgOpen(false)}
          onStart={(knobs) =>
            allSweep.mutate(
              { brandId, data: { knobs } },
              { onSuccess: () => setAllCfgOpen(false) },
            )
          }
        />
      )}
    </>
  );

  if (brandQ.isError) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body>
            <ErrorState
              compact
              title="Could not load this brand"
              message="It may have been removed."
              onRetry={() => brandQ.refetch()}
            />
          </Panel.Body>
        </Panel>
      </>
    );
  }

  return (
    <>
      {head}

      <Seg
        className="mb-3.5"
        brand
        value={tab}
        onChange={selectTab}
        options={[
          ...platformKeys().map((p) => ({
          value: p,
          label: (
            <>
              <span className={`plat ${p}`} style={{ pointerEvents: "none" }}>
                {platformLabel(p)}
              </span>
              <span className="num faint" style={{ marginLeft: 7, fontSize: 11 }}>
                {countsQ.isLoading ? "…" : countFor(p).toLocaleString()}
              </span>
            </>
          ),
          })),
          {
            value: MATCH_TAB,
            label: <span>Matched SKUs</span>,
          },
        ]}
      />

      {tab === MATCH_TAB && brandId && <BrandMatchGrid brandId={brandId} />}

      {tab !== MATCH_TAB && brandQ.data &&
        !countsQ.isLoading &&
        countFor(platformTab) === 0 &&
        (brandQ.data.candidates ?? []).some((c) => c.platform === platformTab && c.enabled) && (
          <Panel className="mb-3.5" style={{ borderColor: "var(--warn)" }}>
            <Panel.Body
              style={{
                color: "var(--warn)",
                fontSize: 12.5,
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                lineHeight: 1.5,
              }}
            >
              <span aria-hidden style={{ fontSize: 14, lineHeight: 1.2 }}>⚠</span>
              <span>
                A {platformLabel(platformTab)} search candidate is configured but{" "}
                <strong>no SKUs matched</strong>. If a sweep has already run, the storefront's brand
                label likely differs from your search term: <strong>Test</strong> the candidate below
                and click a brand under "matching brands (click to whitelist)" to add it as an alias,
                then re-run the sweep. If no sweep has run yet, this clears after the next one.
              </span>
            </Panel.Body>
          </Panel>
        )}

      {tab !== MATCH_TAB && brandQ.data && (
        <CandidatesPanel
          key={`cand-${platformTab}`}
          brandId={brandId}
          brandName={brand}
          platform={platformTab}
          candidates={(brandQ.data.candidates ?? []).filter((c) => c.platform === platformTab)}
        />
      )}

      {tab !== MATCH_TAB && brand && (
        <PlatformSkus
          key={platformTab}
          platform={platformTab}
          brand={brand}
          brandId={brandId}
          count={countFor(platformTab)}
        />
      )}
    </>
  );
}

// Manage one platform's search candidates: edit term/aliases, live-test, save, delete.
function CandidatesPanel({
  brandId,
  brandName,
  platform,
  candidates,
}: {
  brandId: string;
  brandName: string;
  platform: Platform;
  candidates: BrandCandidateOut[];
}) {
  const qc = useQueryClient();
  const notify = useToast();
  const canWrite = useCanWrite();
  const refresh = () =>
    qc.invalidateQueries({
      queryKey: getGetBrandV1BrandsBrandIdGetQueryKey(brandId),
    });

  const add = useAddCandidateV1BrandsBrandIdCandidatesPost({
    mutation: {
      onSuccess: () => {
        notify("candidate added");
        refresh();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "add failed", "crit"),
    },
  });

  // Ad-hoc "sweep this brand on <platform>" (planning/41): all enabled candidates on
  // this tab, authoritative full sweep. Disabled when there is no enabled candidate.
  const brandSweep = useSweepLauncher();
  const [cfgOpen, setCfgOpen] = useState(false);
  const hasEnabled = candidates.some((c) => c.enabled);
  const runnable = platformSupports(platform, Flow.brand_sweep);

  return (
    <Panel className="mb-3.5">
      <Panel.Head title="Search candidates">
        <span className={`plat ${platform}`} style={{ marginLeft: 8 }}>{platformLabel(platform)}</span>
        <div className="flex-1" />
        {canWrite && <Button
          variant="primary"
          size="sm"
          loading={brandSweep.isPending}
          disabled={!hasEnabled || !runnable}
          title={
            !runnable
              ? `${platformLabel(platform)} brand sweep is unavailable in this deployment`
              : hasEnabled
              ? `Launch a one-off sweep of ${brandName} on ${platformLabel(platform)}`
              : `No enabled ${platformLabel(platform)} candidate to sweep`
          }
          onClick={() => setCfgOpen(true)}
        >
          {brandSweep.isPending ? (
            "Starting…"
          ) : (
            <>
              Sweep {brandName} on {platformLabel(platform)} <PlayIcon />
            </>
          )}
        </Button>}
        {canWrite && cfgOpen && (
          <SweepConfigModal
            scopeLabel={`Sweep ${brandName} on ${platformLabel(platform)}`}
            note="All enabled candidates on this platform (brand-level, authoritative)."
            targetPlatforms={[platform]}
            pending={brandSweep.isPending}
            onClose={() => setCfgOpen(false)}
            onStart={(knobs) =>
              brandSweep.mutate(
                { brandId, data: { platform, knobs } },
                { onSuccess: () => setCfgOpen(false) },
              )
            }
          />
        )}
        {canWrite && <Button
          variant="outline"
          size="sm"
          loading={add.isPending}
          disabled={!runnable}
          title={
            runnable
              ? `Add a ${platformLabel(platform)} search candidate`
              : `${platformLabel(platform)} brand sweep is unavailable`
          }
          onClick={() =>
            add.mutate({
              brandId,
              data: {
                platform,
                search_term: brandName,
                match_aliases: [],
                enabled: true,
              },
            })
          }
        >
          + Add candidate
        </Button>}
      </Panel.Head>
      <Panel.Body style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {candidates.length === 0 && (
          <div className="muted" style={{ padding: 8, textAlign: "center" }}>
            No {platformLabel(platform)} candidate: add one and test it against the live search.
          </div>
        )}
        {candidates.map((c) => (
          <CandidateRow key={c.id} brandId={brandId} cand={c} onChanged={refresh} />
        ))}
      </Panel.Body>
    </Panel>
  );
}

// Pull the numeric platform brand_id out of a persisted brand-catalog ref:
// the ref carries `collection_filters` = '[{"brand_id":[2700]}]'. Null when unresolved,
// malformed, or the `{"none": true}` "no brand tile" sentinel.
function catalogBrandId(ref: BrandCandidateOut["platform_brand_ref"]): number | null {
  const cf = (ref as Record<string, unknown> | null | undefined)?.collection_filters;
  if (typeof cf !== "string") return null;
  try {
    const parsed = JSON.parse(cf) as Array<{ brand_id?: number[] }>;
    return parsed?.[0]?.brand_id?.[0] ?? null;
  } catch {
    return null;
  }
}

// The resolved platform brand_id + an "open on <platform>" search link.
// Only the search URL is surfaced, a brand *collection* is a client-side-only app
// deeplink (cold web paths 404), so we never construct a fake collection link. Renders
// only for a platform whose contract carries a brand search URL (Blinkit today).
function BrandCatalogRef({ cand }: { cand: BrandCandidateOut }) {
  const searchUrl = brandSearchUrl(cand.platform, cand.search_term);
  if (!searchUrl) return null;
  const bid = catalogBrandId(cand.platform_brand_ref);
  const isNone = Boolean((cand.platform_brand_ref as Record<string, unknown> | null)?.none);
  return (
    <div className="row" style={{ gap: 10, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
      {bid != null ? (
        <span className="faint" style={{ fontSize: 11 }}>
          brand_id <b className="num" style={{ color: "var(--ink-2)" }}>{bid}</b>
          {cand.brand_ref_resolved_at && <> · resolved {ago(cand.brand_ref_resolved_at)}</>}
        </span>
      ) : isNone ? (
        <span className="faint" style={{ fontSize: 11 }}>no brand catalog · sweeps via text search</span>
      ) : (
        <span className="faint" style={{ fontSize: 11 }}>brand_id unresolved · resolves on next sweep</span>
      )}
      <Button asChild size="sm">
      <a
        href={searchUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open the ${platformLabel(cand.platform)} search view in a new tab (includes rival/sponsored cards, not the exact brand catalog)`}
      >
        Search on {platformLabel(cand.platform)} ↗
      </a>
      </Button>
    </div>
  );
}

function CandidateRow({
  brandId,
  cand,
  onChanged,
}: {
  brandId: string;
  cand: BrandCandidateOut;
  onChanged: () => void;
}) {
  const notify = useToast();
  const canWrite = useCanWrite();
  const [term, setTerm] = useState(cand.search_term);
  const [aliases, setAliases] = useState<string[]>(cand.match_aliases);
  const [result, setResult] = useState<SearchProbeResult | null>(null);
  const [testing, setTesting] = useState(false);
  const runnable = platformSupports(cand.platform, Flow.brand_sweep);
  const dirty = term !== cand.search_term || aliases.join("|") !== cand.match_aliases.join("|");

  // The live term, readable from an in-flight probe's closure (which captured the old one).
  const termRef = useRef(term);
  useEffect(() => {
    termRef.current = term;
  }, [term]);

  const test = async (termOverride?: string) => {
    const q = (termOverride ?? term).trim();
    if (!q) return;
    setTesting(true);
    setResult(null);
    try {
      const res = await searchProbeV1BrandsSearchProbePost({
        platform: cand.platform,
        search_term: q,
        match_aliases: aliases,
      });
      // A probe walk takes seconds; if the operator edited the term meanwhile, dropping
      // the result keeps Save from stamping `verified` + a hit sample onto a term that
      // was never probed.
      if (termRef.current.trim() === q) setResult(res);
    } catch (e) {
      notify(e instanceof Error ? e.message : "probe failed", "crit");
    } finally {
      setTesting(false);
    }
  };

  const save = useUpdateCandidateV1BrandsBrandIdCandidatesCandidateIdPatch({
    mutation: {
      onSuccess: () => {
        notify("candidate saved", "ok");
        onChanged();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "save failed", "crit"),
    },
  });

  const toggle = useUpdateCandidateV1BrandsBrandIdCandidatesCandidateIdPatch({
    mutation: {
      onSuccess: onChanged,
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });

  const del = useDeleteCandidateV1BrandsBrandIdCandidatesCandidateIdDelete({
    mutation: {
      onSuccess: () => {
        notify("candidate removed");
        onChanged();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "delete failed", "crit"),
    },
  });

  // Ad-hoc "run this keyword" (planning/41): a one-off, additive-only sweep of just this
  // saved search term across all stores. Only for an enabled candidate; uses the saved
  // term (not the unsaved edit buffer) so we never launch something not persisted.
  const kwSweep = useSweepLauncher();
  const [kwCfgOpen, setKwCfgOpen] = useState(false);

  const addAlias = (a: string) => {
    if (!aliases.some((x) => x.toLowerCase() === a.toLowerCase())) setAliases([...aliases, a]);
  };

  if (!canWrite) {
    return (
      <Panel style={{ padding: 10, background: "var(--bg-2)", border: "1px solid var(--line)" }}>
        <Row gap="sm" wrap>
          <span className="strong">{cand.search_term}</span>
          <Badge variant={cand.enabled ? "outline-success" : "outline"}>
            {cand.enabled ? "enabled" : "disabled"}
          </Badge>
          {cand.verified_at && (
            <Badge variant="outline-success" title={`verified ${ago(cand.verified_at)}`}>
              verified, {cand.last_test_hit_count ?? 0} hits
            </Badge>
          )}
        </Row>
        {cand.match_aliases.length > 0 && (
          <div className="faint" style={{ marginTop: 8, fontSize: 11 }}>
            aliases: {cand.match_aliases.join(", ")}
          </div>
        )}
        <BrandCatalogRef cand={cand} />
      </Panel>
    );
  }

  return (
    <Panel
      style={{
        padding: 10,
        background: "var(--bg-2)",
        border: "1px solid var(--line)",
      }}
    >
      <Row gap="sm" wrap>
        <Input
          style={{ flex: 1, minWidth: 150 }}
          value={term}
          onChange={(e) => setTerm(e.currentTarget.value)}
        />
        {cand.verified_at ? (
          <Badge variant="outline-success" style={{ fontSize: 10.5 }} title={`verified ${ago(cand.verified_at)}`}>
            ✓ {cand.last_test_hit_count ?? 0}
          </Badge>
        ) : (
          <Badge variant="outline" style={{ fontSize: 10.5 }}>unverified</Badge>
        )}
        <Switch
          checked={cand.enabled}
          disabled={toggle.isPending || (!runnable && !cand.enabled)}
          aria-label="toggle candidate enabled"
          onCheckedChange={() =>
            toggle.mutate({
              brandId,
              candidateId: cand.id,
              data: { enabled: !cand.enabled },
            })
          }
        />
        <Button
          variant="primary"
          size="sm"
          loading={testing}
          disabled={!term.trim() || !runnable}
          title={runnable ? "Test this search term live" : "Brand sweep is unavailable"}
          onClick={() => test()}
        >
          {testing ? "Testing…" : <>Test <PlayIcon /></>}
        </Button>
        <Button
          variant="success"
          size="sm"
          // enabled when edited, or after a passing probe (to stamp verification)
          loading={save.isPending}
          disabled={!dirty && !result?.found}
          onClick={() =>
            save.mutate({
              brandId,
              candidateId: cand.id,
              data: {
                search_term: term.trim(),
                match_aliases: aliases,
                ...(result?.found
                  ? {
                      verified: true,
                      last_test_hit_count: result.hit_count,
                      last_test_sample: result.products,
                    }
                  : {}),
              },
            })
          }
        >
          Save
        </Button>
        {cand.enabled && (
          <Button
            size="sm"
            loading={kwSweep.isPending}
            disabled={dirty || !runnable}
            title={
              !runnable
                ? `${platformLabel(cand.platform)} brand sweep is unavailable`
                : dirty
                ? "Save the candidate first, this runs the saved keyword"
                : `Run a one-off sweep for "${cand.search_term}" across all stores`
            }
            onClick={() => setKwCfgOpen(true)}
          >
            {kwSweep.isPending ? "Starting…" : <>Run <PlayIcon /></>}
          </Button>
        )}
        {kwCfgOpen && (
          <SweepConfigModal
            scopeLabel={`Run "${cand.search_term}" on ${platformLabel(cand.platform)}`}
            note="Single keyword across all stores (additive-only; never retires SKUs)."
            targetPlatforms={[cand.platform]}
            pending={kwSweep.isPending}
            onClose={() => setKwCfgOpen(false)}
            onStart={(knobs) =>
              kwSweep.mutate(
                {
                  brandId,
                  data: {
                    platform: cand.platform,
                    search_term: cand.search_term,
                    knobs,
                  },
                },
                { onSuccess: () => setKwCfgOpen(false) },
              )
            }
          />
        )}
        <Button
          variant="destructive"
          size="sm"
          loading={del.isPending}
          onClick={() => del.mutate({ brandId, candidateId: cand.id })}
        >
          ✕
        </Button>
      </Row>

      {aliases.length > 0 && (
        <div
          className="row"
          style={{
            gap: 6,
            marginTop: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span className="faint" style={{ fontSize: 11 }}>
            aliases:
          </span>
          {aliases.map((a) => (
            <Badge key={a} variant="outline-primary" style={{ fontSize: 11 }}>
              {a}
              <button
                style={{
                  marginLeft: 4,
                  background: "none",
                  border: 0,
                  color: "inherit",
                  cursor: "pointer",
                }}
                onClick={() => setAliases(aliases.filter((x) => x !== a))}
              >
                ✕
              </button>
            </Badge>
          ))}
        </div>
      )}

      <BrandCatalogRef cand={cand} />

      {result && (
        <ProbeReport
          result={result}
          platform={cand.platform}
          onAddAlias={addAlias}
          // Swapping the term leaves the candidate dirty; Save persists it once the
          // re-test passes (the probe below reflects the new term immediately).
          onUseTerm={(t) => {
            setTerm(t);
            void test(t);
          }}
        />
      )}
    </Panel>
  );
}

// One platform's active-SKU list + lifecycle timeline for a brand.
// planning/47: the master enable/disable toggle for a SKU (skips it in BOTH cart inventory
// and store-sku-map scrapes). Distinct from "Remove" (the terminal excluded status).
function SkuEnabledCell({
  platform,
  brandId,
  productId,
  enabled,
  onChanged,
}: {
  platform: Platform;
  brandId: string;
  productId: string;
  enabled: boolean;
  onChanged: () => void;
}) {
  const notify = useToast();
  const setEnabled = useSetSkuEnabledV1BrandsBrandIdSkusSetEnabledPost({
    mutation: {
      onSuccess: (_r, vars) => {
        notify(vars.data.enabled ? "Tracking this SKU" : "Stopped tracking (skipped by scrapes)", "ok");
        onChanged();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });
  return (
    <Switch
      checked={enabled}
      disabled={setEnabled.isPending}
      aria-label={enabled ? "Tracked: scraped by cart + store-map" : "Not tracked: skipped by both scrapes"}
      title={
        enabled
          ? "Tracked: scraped by cart inventory + store-sku map. Click to stop tracking."
          : "Not tracked: skipped by both scrapes. Click to start tracking."
      }
      onCheckedChange={(next) =>
        setEnabled.mutate({
          brandId,
          data: { platform, product_ids: [productId], enabled: next },
        })
      }
    />
  );
}

// planning/47: mark/unmark a SKU as a bundle (excludes it from probing, like a scraped
// multipack) + the terminal "Remove" action.
function SkuRowActions({
  platform,
  brandId,
  productId,
  isBundle,
  onChanged,
}: {
  platform: Platform;
  brandId: string;
  productId: string;
  isBundle: boolean;
  onChanged: () => void;
}) {
  const notify = useToast();
  const setBundle = useSetSkuBundleV1BrandsBrandIdSkusSetBundlePost({
    mutation: {
      onSuccess: (_r, vars) => {
        notify(vars.data.is_bundle ? "Marked as bundle" : "Unmarked bundle", "ok");
        onChanged();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });
  return (
    <span className="row" style={{ gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
      <Button
        variant="outline"
        size="sm"
        loading={setBundle.isPending}
        title={
          isBundle
            ? "Clear the manual bundle flag (probe this SKU again)"
            : "Mark as a bundle: excludes it from cart/store-map probing (double-count guard)"
        }
        onClick={() =>
          setBundle.mutate({
            brandId,
            data: { platform, product_ids: [productId], is_bundle: !isBundle },
          })
        }
      >
        {isBundle ? "Unbundle" : "Mark bundle"}
      </Button>
      <RemoveSkuCell platform={platform} brandId={brandId} productId={productId} onRemoved={onChanged} />
    </span>
  );
}

function PlatformSkus({ platform, brand, brandId, count }: { platform: Platform; brand: string; brandId: string; count: number }) {
  const qc = useQueryClient();
  const notify = useToast();
  const canWrite = useCanWrite();
  const [addOpen, setAddOpen] = useState(false);
  const skusQ = useBrandSkusV1BrandsSkusGet({ platform, brand, limit: 2000 });
  const eventsQ = useSkuEventsV1SkuEventsGet({ platform, brand, limit: 100 });
  // Stable array refs so the combo useMemos below don't recompute every render.
  const skus = useMemo(() => skusQ.data ?? [], [skusQ.data]);
  const events = eventsQ.data ?? [];

  // Combos (bundle/multipack SKUs that alias a base product) are hidden by default so the
  // table shows distinct base products; a toggle brings them back. `is_bundle` is the
  // effective value (operator override already merged server-side).
  const [showCombos, setShowCombos] = useState(false);
  const [showOnlyTracked, setShowOnlyTracked] = useState(false);
  const comboCount = useMemo(() => skus.reduce((n, s) => n + (s.is_bundle ? 1 : 0), 0), [skus]);
  const shownSkus = useMemo(
    () => skus.filter((s) => (showCombos || !s.is_bundle) && (!showOnlyTracked || (s.enabled ?? true))),
    [skus, showCombos, showOnlyTracked],
  );

  // Bulk selection over the table's filtered+sorted rows (so it respects the toolbar filters
  // and the search box), keyed by product_id. Mirrors the Brands page's selection pattern:
  // the header checkbox only touches the rows in view, so rows selected under a different
  // search stay selected.
  const [visibleRows, setVisibleRows] = useState<Sku[]>([]);
  const {
    selected,
    selectedIds,
    allSelected,
    someSelected,
    hiddenCount,
    toggleAll,
    toggleOne,
    clear: clearSelection,
  } = useRowSelection(visibleRows, (s) => s.product_id);

  // A manual add/remove lands in {p}_skus / {p}_sku_events: refresh both + the tab counts.
  const refreshSkus = () => {
    clearSelection();
    qc.invalidateQueries({
      queryKey: getBrandSkusV1BrandsSkusGetQueryKey({
        platform,
        brand,
        limit: 2000,
      }),
    });
    qc.invalidateQueries({
      queryKey: getSkuEventsV1SkuEventsGetQueryKey({
        platform,
        brand,
        limit: 100,
      }),
    });
    qc.invalidateQueries({ queryKey: ["sku-counts"] });
  };

  const skuColumns = useMemo<AnyColumnDef<Sku>[]>(() => {
    const col = createColumnHelper<Sku>();
    const columns: AnyColumnDef<Sku>[] = [
      col.display({
        id: "select",
        header: () => (
          <SelectAllCheckbox
            checked={allSelected}
            indeterminate={someSelected}
            hiddenCount={hiddenCount}
            onChange={toggleAll}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={`select ${row.original.name || row.original.product_id}`}
            checked={selected.has(row.original.product_id)}
            onChange={() => toggleOne(row.original.product_id)}
          />
        ),
        meta: { align: "center", width: 40 },
        enableSorting: false,
        enableHiding: false,
      }),
      col.accessor("name", {
        id: "sku",
        header: "SKU",
        cell: ({ row }) => {
          const s = row.original;
          return (
            <div className="row" style={{ gap: 8, alignItems: "center" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 30,
                  height: 30,
                  flex: "0 0 auto",
                  borderRadius: 5,
                  border: "1px solid var(--line)",
                  background: "var(--img-plate)",
                  overflow: "hidden",
                }}
              >
                {s.image_url ? (
                  <img
                    src={s.image_url}
                    alt={s.name || s.product_id}
                    loading="lazy"
                    width={28}
                    height={28}
                    style={{ objectFit: "contain" }}
                  />
                ) : (
                  <span className="faint" style={{ fontSize: 12 }}>—</span>
                )}
              </div>
              <div className="col" style={{ gap: 1, minWidth: 0 }}>
                <div
                  className="strong"
                  style={{
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Link
                    to={`/skus/${platform}/${encodeURIComponent(s.product_id)}?from=${brandId}`}
                    style={{ color: "inherit", textDecoration: "none" }}
                    title="View SKU details"
                  >
                    {s.name || "—"}
                  </Link>
                  <a
                    href={pdpUrl(platform, s.product_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open product page on the storefront"
                    aria-label="Open product page in a new tab"
                    style={{
                      color: "var(--info)",
                      display: "inline-flex",
                      fontSize: 12,
                      lineHeight: 0,
                    }}
                  >
                    <IconExternal />
                  </a>
                  {/* Two DIFFERENT bundle shapes, and only one of them has a base to link to
                      (planning/59). A multipack is N of one product, so it links to that base.
                      A COMBO is several different products pooled under one SKU: it has no base
                      by construction, so gating the pill on base_product_id rendered nothing at
                      all for every combo -- invisible in the UI despite being excluded from
                      scraping. A standalone N-pack whose base we never discovered (is_bundle=0)
                      is still a normal, tracked SKU and shows no pill; its pack size stays
                      visible in the Pack column. is_bundle also honors an operator
                      force-NOT-bundle override. */}
                  {s.is_bundle && s.bundle_kind === "combo" ? (
                    <Badge
                      variant="outline-warning"
                      style={{ fontSize: 10 }}
                      title="Combo: several different products under one SKU, sharing their stock. The platform does not say which, so there is no base to read instead -- it is excluded from inventory rather than redirected."
                    >
                      Combo
                    </Badge>
                  ) : s.is_bundle && s.base_product_id ? (
                    <Badge asChild variant="outline-warning">
                      <Link
                        to={`/skus/${platform}/${encodeURIComponent(s.base_product_id)}?from=${brandId}`}
                        style={{ fontSize: 10, textDecoration: "none" }}
                      title={`Combo of ${s.base_pack_size || `base SKU ${s.base_product_id}`} (excluded from inventory to avoid double-counting the base)`}
                    >
                        Pack of {s.pack_count}
                      </Link>
                    </Badge>
                  ) : null}
                </div>
                <div className="mono faint" style={{ fontSize: 10.5 }}>{s.product_id}</div>
              </div>
            </div>
          );
        },
        meta: {
          minWidth: 300,
          exportValue: (s) => s.name || s.product_id,
          searchValue: (s) => `${s.name ?? ""} ${s.product_id}`,
        },
      }),
      col.accessor((s) => s.pack_size || "", {
        id: "pack",
        header: "Pack",
        cell: ({ row }) => <span className="faint" style={{ fontSize: 11.5 }}>{row.original.pack_size || "—"}</span>,
        meta: { width: 120 },
      }),
      col.accessor((s) => (s.category_path ?? []).join(" › "), {
        id: "category",
        header: "Category",
        cell: ({ getValue }) => <span className="faint" style={{ fontSize: 11.5 }}>{(getValue() as string) || "—"}</span>,
        meta: { minWidth: 160 },
      }),
      col.accessor((s) => s.product_type || "", {
        id: "product_type",
        header: "Type",
        cell: ({ getValue }) => <span className="faint" style={{ fontSize: 11.5 }}>{(getValue() as string) || "—"}</span>,
        meta: { minWidth: 120, filterSelect: true, filterLabel: "Type" },
      }),
      col.accessor((s) => (s.taxonomy_path ?? []).join(" › "), {
        id: "taxonomy",
        header: "Taxonomy",
        cell: ({ row, getValue }) => {
          const path = getValue() as string;
          return path ? (
            <Link
              to={`/skus/${platform}/${encodeURIComponent(row.original.product_id)}?from=${brandId}`}
              className="faint"
              style={{ fontSize: 11.5, textDecoration: "none" }}
              title="Canonical taxonomy path (classified): open SKU"
            >
              {path}
            </Link>
          ) : (
            <span className="faint" style={{ fontSize: 11.5 }}>—</span>
          );
        },
        meta: { minWidth: 180 },
      }),
      col.accessor((s) => (s.is_bundle ? "Combo" : "Single"), {
        id: "bundle",
        header: "Combo",
        cell: ({ getValue, row }) => {
          const v = getValue() as string;
          if (v !== "Combo") return <span className="faint" style={{ fontSize: 11 }}>Single</span>;
          const s = row.original;
          // "Combo of <base>" -- the base SKU this multipack aggregates. base_pack_size is the
          // readable label ("200 ml"); base_product_id links to the base row when it is one of
          // our discovered SKUs. Falls back to the raw stated ref when the base is off-catalog.
          const baseLabel = s.base_pack_size || s.base_product_id || s.base_product_ref || "";
          return (
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <Badge variant="outline-warning" style={{ fontSize: 10 }}>
                Combo
              </Badge>
              {baseLabel ? (
                s.base_product_id ? (
                  <Link
                    to={`/skus/${platform}/${encodeURIComponent(s.base_product_id)}?from=${brandId}`}
                    className="faint"
                    style={{
                      fontSize: 10.5,
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                    title={`Combo of base SKU ${s.base_product_id}`}
                  >
                    of {baseLabel}
                  </Link>
                ) : (
                  <span className="faint" style={{ fontSize: 10.5, whiteSpace: "nowrap" }} title="base SKU not in this catalog">
                    of {baseLabel}
                  </span>
                )
              ) : null}
            </div>
          );
        },
        meta: { minWidth: 150 },
      }),
      col.accessor((s) => s.manufacturer || "", {
        id: "manufacturer",
        header: "Manufacturer",
        cell: ({ row }) => <span className="faint" style={{ fontSize: 11.5 }}>{row.original.manufacturer || "—"}</span>,
        meta: { minWidth: 160 },
      }),
      col.accessor("last_seen_at", {
        id: "last_seen",
        header: "Last seen",
        // Date included: a brand sweep stamps one timestamp across every SKU it rediscovers,
        // so a time alone cannot say whether that sweep ran an hour or a week ago.
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11 }} title={timestampTitle(row.original.last_seen_at)}>
            {fmtDateTime(row.original.last_seen_at)}
          </span>
        ),
        meta: { align: "right", width: 150, exportValue: (s) => s.last_seen_at },
      }),
      col.display({
        id: "enabled",
        header: "Track",
        cell: ({ row }) =>
          canWrite ? (
            <SkuEnabledCell
              platform={platform}
              brandId={brandId}
              productId={row.original.product_id}
              enabled={row.original.enabled ?? true}
              onChanged={refreshSkus}
            />
          ) : (
            <Badge variant={(row.original.enabled ?? true) ? "outline-success" : "outline"}>
              {(row.original.enabled ?? true) ? "tracked" : "untracked"}
            </Badge>
          ),
        meta: { align: "center", width: 90 },
        enableSorting: false,
        enableHiding: false,
      }),
      col.display({
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <SkuRowActions
            platform={platform}
            brandId={brandId}
            productId={row.original.product_id}
            isBundle={row.original.is_bundle ?? false}
            onChanged={refreshSkus}
          />
        ),
        meta: { align: "right", width: 200 },
        enableSorting: false,
        enableHiding: false,
      }),
    ];
    return canWrite
      ? columns
      : columns.filter((column) => column.id !== "select" && column.id !== "actions");
    // platform/brandId are stable for this mounted tab; refreshSkus only closes
    // over stable query-client + params, so it need not force a re-memo. The selection
    // state DOES have to re-memo, or the checkboxes never re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, brandId, selected, allSelected, someSelected, hiddenCount, canWrite]);

  const eventColumns = useMemo<AnyColumnDef<SkuEvent>[]>(() => {
    const col = createColumnHelper<SkuEvent>();
    return [
      col.accessor("event_at", {
        id: "when",
        header: "When",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11.5 }} title={timestampTitle(row.original.event_at)}>
            {fmtDateTime(row.original.event_at)}
          </span>
        ),
        meta: { width: 170, exportValue: (e) => e.event_at },
      }),
      col.accessor("event_type", {
        id: "event",
        header: "Event",
        cell: ({ row }) => (
          <Badge variant={toneVariant(EVENT_TONE[row.original.event_type] ?? "idle")} style={{ fontSize: 10.5 }}>
            {row.original.event_type}
          </Badge>
        ),
        meta: { width: 140, filterSelect: true, filterLabel: "Event" },
      }),
      // prev -> new status: already on the wire, and it is the "what actually changed"
      // column the raw event type only hints at.
      col.accessor((e) => `${e.prev_status || "\u2205"} \u2192 ${e.new_status || "\u2205"}`, {
        id: "transition",
        header: "Transition",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11 }}>
            {row.original.prev_status || "∅"} → {row.original.new_status || "∅"}
          </span>
        ),
        meta: { minWidth: 130 },
        enableSorting: false,
      }),
      // Name + id + pack + combo pill, same shape as the SKU page's store cell. A bare
      // product_id told an operator nothing; this also makes the global search useful,
      // since it matches every accessor.
      col.accessor((e) => e.name || e.product_id, {
        id: "product",
        header: "Product",
        cell: ({ row }) => {
          const e = row.original;
          return (
            <div className="col" style={{ gap: 1, minWidth: 0 }}>
              <div className="row" style={{ gap: 6, alignItems: "center" }}>
                <Link
                  to={`/skus/${platform}/${encodeURIComponent(e.product_id)}?from=${brandId}`}
                  className="strong"
                  style={{
                    fontSize: 11.5,
                    color: "inherit",
                    textDecoration: "none",
                  }}
                  title="View SKU details"
                >
                  {e.name || "\u2014"}
                </Link>
                {e.is_bundle && (
                  <Badge variant="outline-warning" style={{ fontSize: 9.5 }}>Combo</Badge>
                )}
                {e.pack_size && (
                  <span className="faint" style={{ fontSize: 10.5 }}>{e.pack_size}</span>
                )}
              </div>
              <span className="mono faint" style={{ fontSize: 10 }}>{e.product_id}</span>
            </div>
          );
        },
        meta: { minWidth: 240, exportValue: (e) => e.name || e.product_id },
      }),
      col.accessor("run_id", {
        id: "run",
        header: "Run",
        cell: ({ row }) =>
          row.original.run_id.startsWith("manual") ? (
            <span className="mono faint" style={{ fontSize: 10.5 }} title="Added manually by an operator">manual</span>
          ) : (
            <Link className="mono faint" to={`/runs/${row.original.run_id}`} style={{ fontSize: 10.5 }}>
              {row.original.run_id.slice(-12)}
            </Link>
          ),
        meta: { minWidth: 140 },
      }),
    ];
  }, [platform, brandId]);

  // Bulk track/bundle: both endpoints already take a product_id list and do a single
  // multi-values upsert, so each button is one request no matter how many rows.
  const bulkEnabled = useSetSkuEnabledV1BrandsBrandIdSkusSetEnabledPost({
    mutation: {
      onSuccess: (r, vars) => {
        notify(`${vars.data.enabled ? "Tracking" : "Stopped tracking"} ${r.updated} SKUs`, "ok");
        refreshSkus();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });
  const bulkBundle = useSetSkuBundleV1BrandsBrandIdSkusSetBundlePost({
    mutation: {
      onSuccess: (r, vars) => {
        notify(
          vars.data.is_bundle
            ? `Marked ${r.updated} SKUs as combos (and untracked them)`
            : `Unmarked ${r.updated} SKUs`,
          "ok",
        );
        refreshSkus();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });
  const bulkBusy = bulkEnabled.isPending || bulkBundle.isPending;
  const comboIds = useMemo(
    () => skus.filter((s) => s.is_bundle && (s.enabled ?? true)).map((s) => s.product_id),
    [skus],
  );

  return (
    <>
      <Panel className="mb-3.5">
        <Panel.Head title={selected.size > 0 ? undefined : "Active SKUs"}>
          {canWrite && selected.size > 0 && (
            <>
              <SelectionCount count={selected.size} hiddenCount={hiddenCount} />
              <Button
                variant="success"
                size="sm"
                style={{ marginLeft: 10 }}
                disabled={bulkBusy}
                title="Scrape these SKUs in cart inventory + store-SKU map"
                onClick={() =>
                  bulkEnabled.mutate({
                    brandId,
                    data: { platform, product_ids: selectedIds, enabled: true },
                  })
                }
              >
                Track
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={bulkBusy}
                title="Skip these SKUs in both scrapes"
                onClick={() =>
                  bulkEnabled.mutate({
                    brandId,
                    data: {
                      platform,
                      product_ids: selectedIds,
                      enabled: false,
                    },
                  })
                }
              >
                Untrack
              </Button>
              <Button
                size="sm"
                disabled={bulkBusy}
                title="Mark as combos - excludes them from probing and untracks them"
                onClick={() =>
                  bulkBundle.mutate({
                    brandId,
                    data: {
                      platform,
                      product_ids: selectedIds,
                      is_bundle: true,
                    },
                  })
                }
              >
                Mark bundle
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={bulkBusy}
                title="Clear the manual combo flag (tracking is left as-is)"
                onClick={() =>
                  bulkBundle.mutate({
                    brandId,
                    data: {
                      platform,
                      product_ids: selectedIds,
                      is_bundle: false,
                    },
                  })
                }
              >
                Unbundle
              </Button>
              <Button variant="outline" size="sm" disabled={bulkBusy} onClick={clearSelection}>
                Clear
              </Button>
            </>
          )}
          <span className={`plat ${platform}`} style={{ marginLeft: 8 }}>{platformLabel(platform)}</span>
          <div className="flex-1" />
          {canWrite && comboIds.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={bulkBusy}
              title={`Stop tracking every combo SKU loaded here (${comboIds.length} still tracked). Applies to the loaded rows only.`}
              onClick={() =>
                bulkEnabled.mutate({
                  brandId,
                  data: { platform, product_ids: comboIds, enabled: false },
                })
              }
            >
              Untrack all combos ({comboIds.length})
            </Button>
          )}
          {/* `count` excludes combos (server-side); add the loaded combo rows back when the
              table is showing them, so the header never contradicts the rows below it. */}
          <span className="num faint" style={{ fontSize: 11, marginRight: 10 }}>
            {(showCombos ? count + comboCount : count).toLocaleString()} active
          </span>
          {canWrite && platformSupportsManualPdp(platform) && (
            <Button
              variant={addOpen ? "outline" : "primary"}
              size="sm"
              onClick={() => setAddOpen((v) => !v)}
            >
              {addOpen ? "Close" : "+ Add SKU by URL"}
            </Button>
          )}
        </Panel.Head>
        {canWrite && addOpen && platformSupportsManualPdp(platform) && (
          <AddSkuByUrl
            brandId={brandId}
            platform={platform}
            onAdded={() => {
              refreshSkus();
              setAddOpen(false);
            }}
          />
        )}
        <Panel.Body tight>
          <DataTable
            tableKey="brand-skus"
            columns={skuColumns}
            data={shownSkus}
            getRowId={(s) => s.product_id}
            onFilteredRowsChange={setVisibleRows}
            pinFirstColumn
            defaultHidden={["manufacturer", "taxonomy"]}
            globalPlaceholder="Search SKU name / id…"
            enableCsvExport={false}
            toolbarExtras={
              <Row gap="sm" style={{ alignItems: "center" }}>
                <span
                  className="row"
                  style={{ gap: 6, alignItems: "center", fontSize: 11 }}
                  title="Combos are multipacks/bundles that alias a base product (excluded from inventory). Off = show distinct base products only."
                >
                  <Switch
                    checked={showCombos}
                    onCheckedChange={setShowCombos}
                    aria-label="show combo/bundle SKUs"
                    disabled={comboCount === 0}
                  />
                  <span className="faint">show combos{comboCount > 0 ? ` (${comboCount})` : ""}</span>
                </span>
                <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] text-muted-foreground">
                  <Switch
                    checked={showOnlyTracked}
                    onCheckedChange={setShowOnlyTracked}
                    aria-label="Show only tracked"
                  />
                  Show only tracked
                </label>
                <Button asChild variant="outline" size="sm">
                <a
                    href={getBrandSkusExportV1BrandsSkusExportGetUrl({
                      platform,
                      brand,
                    })}
                  title="Download every column of every active SKU (server-side, not just what's shown)"
                >
                  Export CSV (all columns)
                </a>
                </Button>
                <Button asChild variant="outline" size="sm">
                <a
                  href={getBrandSkusExportV1BrandsSkusExportGetUrl({
                    platform,
                    brand,
                    combos: "exclude",
                  })}
                  title="Download every column of active base SKUs only, excluding combo and bundle rows"
                >
                  Export CSV (pure SKUs)
                </a>
                </Button>
              </Row>
            }
            emptyMessage={showOnlyTracked
              ? "No tracked SKUs match these filters. Turn off Show only tracked to see untracked products."
              : `No active SKUs on ${platformLabel(platform)}: run a brand sweep to discover them.`}
            isLoading={skusQ.isLoading}
          />
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Head title="Lifecycle timeline">
          <div className="flex-1" />
          <span className="num faint" style={{ fontSize: 11 }}>{events.length} events</span>
        </Panel.Head>
        <Panel.Body tight>
          <DataTable
            tableKey="brand-events"
            columns={eventColumns}
            data={events}
            getRowId={(e, i) => `${e.product_id}-${e.event_at}-${e.run_id}-${i}`}
            globalPlaceholder="Search events…"
            csvFilename={`${brand}-${platform}-events`}
            emptyMessage={`No SKU events on ${platformLabel(platform)} yet: events appear once a brand sweep runs.`}
            isLoading={eventsQ.isLoading}
          />
        </Panel.Body>
      </Panel>
    </>
  );
}

// The active-SKU Remove action: a two-click confirm that suppresses a false
// positive (sticky "excluded" status the sweep won't resurrect). Lives in the
// SKU table's actions cell, holding its own confirm state per row.
function RemoveSkuCell({
  platform,
  brandId,
  productId,
  onRemoved,
}: {
  platform: Platform;
  brandId: string;
  productId: string;
  onRemoved: () => void;
}) {
  const notify = useToast();
  const [confirming, setConfirming] = useState(false);

  const remove = useRemoveSkuV1BrandsBrandIdSkusRemovePost({
    mutation: {
      onSuccess: (r) => {
        notify(r.status === "already" ? "already removed" : "SKU removed", "ok");
        setConfirming(false);
        onRemoved();
      },
      onError: (e) => {
        notify(e instanceof Error ? e.message : "remove failed", "crit");
        setConfirming(false);
      },
    },
  });

  if (confirming) {
    return (
      <span className="row" style={{ gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
        <span className="faint" style={{ fontSize: 10.5 }}>Remove?</span>
        <Button
          variant="destructive"
          size="sm"
          loading={remove.isPending}
          title="Confirm remove: suppresses this SKU as a false positive"
          onClick={() =>
            remove.mutate({
              brandId,
              data: { platform, product_id: productId },
            })
          }
        >
          {remove.isPending ? "…" : "Yes"}
        </Button>
        <Button variant="outline" size="sm" disabled={remove.isPending} onClick={() => setConfirming(false)}>
          No
        </Button>
      </span>
    );
  }
  return (
    <Button
      variant="outline"
      size="sm"
      title="Remove this SKU as a false positive (won't be re-added by sweeps)"
      onClick={() => setConfirming(true)}
    >
      Remove
    </Button>
  );
}

// Paste a storefront product URL -> live-fetch its PDP -> preview -> add one SKU to
// this brand. Mirrors the candidate Test->Save idiom: the fetch previews what the
// platform's parse_pdp extracts, and the add commits it to the {p}_skus dimension.
function AddSkuByUrl({
  brandId,
  platform,
  onAdded,
}: {
  brandId: string;
  platform: Platform;
  onAdded: () => void;
}) {
  const notify = useToast();
  const [url, setUrl] = useState("");
  const [probing, setProbing] = useState(false);
  const [preview, setPreview] = useState<SkuUrlProbeResult | null>(null);

  const fetchPreview = async () => {
    if (!url.trim()) return;
    setProbing(true);
    setPreview(null);
    try {
      const res = await probeSkuUrlV1BrandsBrandIdSkusProbeUrlPost(brandId, {
        platform,
        url: url.trim(),
      });
      setPreview(res);
    } catch (e) {
      notify(e instanceof Error ? e.message : "fetch failed", "crit");
    } finally {
      setProbing(false);
    }
  };

  const add = useAddSkuUrlV1BrandsBrandIdSkusAddUrlPost({
    mutation: {
      onSuccess: (r) => {
        notify(
          r.status === "refreshed"
            ? "SKU refreshed"
            : r.status === "reactivated"
              ? "SKU re-added"
              : "SKU added",
          "ok",
        );
        setUrl("");
        setPreview(null);
        onAdded();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "add failed", "crit"),
    },
  });

  const f = (preview?.fields ?? {}) as Record<string, unknown>;
  const str = (k: string) => (typeof f[k] === "string" ? (f[k] as string) : "");
  const alreadyActive = preview?.already_status === "active";
  const wasRemoved = preview?.already_status === "removed";
  const wasExcluded = preview?.already_status === "excluded";
  const category = (Array.isArray(f.category_path) ? (f.category_path as string[]) : []).join(" › ");

  return (
    <Panel.Body
      style={{
        borderBottom: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <Row gap="sm" wrap>
        <Input
          style={{ flex: 1, minWidth: 220 }}
          placeholder={`Paste a ${platformLabel(platform)} product URL or id…`}
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") fetchPreview();
          }}
        />
        <Button variant="primary" size="sm" loading={probing} disabled={!url.trim()} onClick={fetchPreview}>
          {probing ? "Fetching…" : <>Fetch <PlayIcon /></>}
        </Button>
      </Row>
      <div className="faint" style={{ fontSize: 11 }}>
        Adds one SKU to this brand from its product page: for products the sweep missed. The
        canonical brand stays this page's brand; the storefront's own label is kept as display_brand.
      </div>

      {preview && !preview.found && (
        <div
          style={{
            fontSize: 12,
            color: preview.error ? "var(--crit)" : "var(--warn)",
          }}
        >
          ✗ {preview.error || "the product page carried no data: try again or check the link"}
          {preview.product_id ? (
            <span className="mono faint" style={{ marginLeft: 6 }}>({preview.product_id})</span>
          ) : null}
        </div>
      )}

      {preview && preview.found && (
        <Panel
          style={{
            padding: 10,
            background: "var(--bg-2)",
            border: "1px solid var(--line)",
          }}
        >
          <Row gap="md" align="start">
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 6,
                border: "1px solid var(--line)",
                background: "var(--img-plate)",
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {str("image_url") ? (
                <img
                  src={str("image_url")}
                  alt={str("name")}
                  width={50}
                  height={50}
                  style={{ objectFit: "contain" }}
                />
              ) : (
                <span className="faint">—</span>
              )}
            </div>
            <div className="col" style={{ gap: 3, flex: 1, minWidth: 0 }}>
              <div className="strong" style={{ fontSize: 13 }}>
                {str("name") || "—"}
              </div>
              <div
                className="row"
                style={{
                  gap: 6,
                  flexWrap: "wrap",
                  alignItems: "center",
                  fontSize: 11,
                }}
              >
                {str("brand") && (
                  <Badge variant="outline-primary" style={{ fontSize: 10.5 }}>
                    {str("brand")}
                  </Badge>
                )}
                {str("pack_size") && <span className="faint">{str("pack_size")}</span>}
                {category && <span className="faint">· {category}</span>}
              </div>
              {str("manufacturer") && (
                <div className="faint" style={{ fontSize: 10.5 }}>{str("manufacturer")}</div>
              )}
              <div className="row" style={{ gap: 6, alignItems: "center", marginTop: 2 }}>
                <span className="mono faint" style={{ fontSize: 10.5 }}>{preview.product_id}</span>
                <a
                  href={pdpUrl(platform, preview.product_id ?? "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open product page in a new tab"
                  style={{
                    color: "var(--info)",
                    display: "inline-flex",
                    fontSize: 12,
                    lineHeight: 0,
                  }}
                >
                  <IconExternal />
                </a>
                {alreadyActive && <Badge variant="outline-success" style={{ fontSize: 10 }}>already tracked</Badge>}
                {wasRemoved && <Badge variant="outline-warning" style={{ fontSize: 10 }}>previously removed</Badge>}
                {wasExcluded && <Badge variant="outline-destructive" style={{ fontSize: 10 }}>previously excluded</Badge>}
              </div>
            </div>
            <Button
              variant="success"
              size="sm"
              style={{ flex: "0 0 auto" }}
              loading={add.isPending}
              disabled={alreadyActive || !preview.product_id}
              onClick={() => add.mutate({ brandId, data: { platform, url: url.trim() } })}
            >
              {alreadyActive
                ? "Tracked"
                : add.isPending
                  ? "Adding…"
                  : wasRemoved || wasExcluded
                    ? "Re-add SKU"
                    : "Add SKU"}
            </Button>
          </Row>
        </Panel>
      )}
    </Panel.Body>
  );
}
